import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { API_BASE_URL } from "../config/api";

export const GoogleDriveSettings: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rootFolderId, setRootFolderId] = useState("");
  const [isLoadingRootFolder, setIsLoadingRootFolder] = useState(true);
  const [isSavingRootFolder, setIsSavingRootFolder] = useState(false);
  const [rootFolderMessage, setRootFolderMessage] = useState("");
  const authStartUrl = `${API_BASE_URL}/google-drive/auth/start`;

  useEffect(() => {
    let isMounted = true;

    const loadStatus = async () => {
      try {
        const status = await apiService.getGoogleDriveAuthStatus();
        if (!isMounted) {
          return;
        }
        setHasStoredCredentials(Boolean(status.hasStoredCredentials));
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setHasStoredCredentials(false);
      } finally {
        if (isMounted) {
          setIsLoadingStatus(false);
        }
      }
    };

    loadStatus();
    const loadRootFolder = async () => {
      try {
        const response = await apiService.getGoogleDriveRootFolder();
        if (!isMounted) {
          return;
        }
        setRootFolderId(response.rootFolderId || "");
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setRootFolderId("");
      } finally {
        if (isMounted) {
          setIsLoadingRootFolder(false);
        }
      }
    };

    loadRootFolder();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAuthorize = async () => {
    if (isLoadingStatus || isSubmitting) {
      return;
    }

    if (hasStoredCredentials) {
      setIsSubmitting(true);
      try {
        await apiService.resetGoogleDriveAuth();
      } catch (error) {
      } finally {
        window.location.href = authStartUrl;
      }
      return;
    }

    window.location.href = authStartUrl;
  };

  const handleSaveRootFolder = async () => {
    if (isLoadingRootFolder || isSavingRootFolder) {
      return;
    }

    setIsSavingRootFolder(true);
    try {
      const response = await apiService.updateGoogleDriveRootFolder(
        rootFolderId.trim()
      );
      setRootFolderId(response.rootFolderId || "");
      setRootFolderMessage(t("common:googleDriveRootSaved"));
    } catch (error) {
      setRootFolderMessage(t("common:googleDriveRootSaveError"));
    } finally {
      setIsSavingRootFolder(false);
      setTimeout(() => setRootFolderMessage(""), 2500);
    }
  };

  const buttonLabel = isLoadingStatus
    ? t("common:loading")
    : hasStoredCredentials
    ? t("common:googleDriveReauthorize")
    : t("common:googleDriveAuthorize");
  const hintText = isLoadingStatus
    ? t("common:loading")
    : hasStoredCredentials
    ? t("common:googleDriveReauthorizeHint")
    : t("common:googleDriveAuthorizeHint");

  return (
    <div className="app-page bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">{hintText}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleAuthorize}
              disabled={isLoadingStatus || isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
            >
              <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              {buttonLabel}
            </button>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("common:googleDriveRootFolder")}
            </h2>
            <p className="text-sm text-gray-600">
              {t("common:googleDriveRootFolderHint")}
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-3">
            <input
              type="text"
              value={rootFolderId}
              onChange={(event) => setRootFolderId(event.target.value)}
              placeholder={
                rootFolderId || t("common:googleDriveRootFolderPlaceholder")
              }
              disabled={isLoadingRootFolder || isSavingRootFolder}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSaveRootFolder}
                disabled={isLoadingRootFolder || isSavingRootFolder}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSavingRootFolder ? t("common:saving") : t("common:save")}
              </button>
              {rootFolderMessage && (
                <span className="text-sm text-slate-600">
                  {rootFolderMessage}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
